import React, { lazy } from 'react';
import { Plus } from 'lucide-react';
import { MenuItem } from '../../lib/types';
import { formatCurrency, cn } from '../../lib/utils';
// Fallback food images by category keyword (optimized for mobile)
const FALLBACK_IMAGES: Record<string, string> = {
  taco: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=300&h=300&fit=crop&q=60',
  tacos: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=300&h=300&fit=crop&q=60',
  burrito: 'https://images.unsplash.com/photo-1626700051175-6818013e1d4f?w=300&h=300&fit=crop&q=60',
  burritos: 'https://images.unsplash.com/photo-1626700051175-6818013e1d4f?w=300&h=300&fit=crop&q=60',
  quesadilla: 'https://images.unsplash.com/photo-1618040996337-56904b7850b9?w=300&h=300&fit=crop&q=60',
  quesadillas: 'https://images.unsplash.com/photo-1618040996337-56904b7850b9?w=300&h=300&fit=crop&q=60',
  burger: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&h=300&fit=crop&q=60',
  hamburguesa: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&h=300&fit=crop&q=60',
  pizza: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=300&h=300&fit=crop&q=60',
  bebida: 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=300&h=300&fit=crop&q=60',
  bebidas: 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=300&h=300&fit=crop&q=60',
  postre: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=300&h=300&fit=crop&q=60',
  postres: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=300&h=300&fit=crop&q=60',
  combo: 'https://images.unsplash.com/photo-1561758033-d89a9ad46330?w=300&h=300&fit=crop&q=60',
  combos: 'https://images.unsplash.com/photo-1561758033-d89a9ad46330?w=300&h=300&fit=crop&q=60',
  entrada: 'https://images.unsplash.com/photo-1541014741259-de529411b96a?w=300&h=300&fit=crop&q=60',
  entradas: 'https://images.unsplash.com/photo-1541014741259-de529411b96a?w=300&h=300&fit=crop&q=60',
  ensalada: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=300&h=300&fit=crop&q=60',
  nacho: 'https://images.unsplash.com/photo-1513456852971-30c0b8199d4d?w=300&h=300&fit=crop&q=60',
  nachos: 'https://images.unsplash.com/photo-1513456852971-30c0b8199d4d?w=300&h=300&fit=crop&q=60',
  sopa: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?w=300&h=300&fit=crop&q=60'
};
const DEFAULT_FOOD_IMAGE = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300&h=300&fit=crop&q=60';
function getFallbackImage(itemName: string): string {
  const lower = itemName.toLowerCase();
  for (const [key, url] of Object.entries(FALLBACK_IMAGES)) {
    if (lower.includes(key)) return url;
  }
  return DEFAULT_FOOD_IMAGE;
}
interface MenuItemCardProps {
  item: MenuItem;
  onClick: (item: MenuItem) => void;
  primaryColor?: string;
  theme?: 'default' | 'capricho';
}
export function MenuItemCard({
  item,
  onClick,
  theme = 'default'
}: MenuItemCardProps) {
  const imageUrl = item.image_url || getFallbackImage(item.name);
  if (theme === 'capricho') {
    return <div onClick={() => onClick(item)} className="relative flex flex-col rounded-2xl overflow-hidden cursor-pointer bg-white border border-[#E8DDD0] shadow-sm hover:shadow-md shadow-[#8B5E3C]/5 active:scale-[0.98] transition-all h-full" data-id="element-715">
        {/* Image Top Half */}
        <div className="relative aspect-[4/3] w-full bg-[#F5E9DA] shrink-0" data-id="element-716">
          <img src={imageUrl} alt={item.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-717" />

          {/* Add Button — floating on the image */}
          <button className="absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center bg-[#8B5E3C] text-white z-10 active:scale-90 transition-transform shadow-md" aria-label="Agregar al pedido" onClick={e => {
          e.stopPropagation();
          onClick(item);
        }} data-id="element-718">
            <Plus className="w-4 h-4" data-id="element-719" />
          </button>
        </div>

        {/* Content Bottom Half */}
        <div className="p-3 sm:p-4 flex flex-col flex-1" data-id="element-720">
          <h3 className="font-bold text-[#3D2B1F] text-[14px] sm:text-base leading-tight line-clamp-2 mb-1" style={{
          fontFamily: "'Playfair Display', serif"
        }} data-id="element-721">
            {item.name}
          </h3>
          {item.description && <p className="text-[11px] sm:text-xs text-[#8B7355] line-clamp-2 mb-2 flex-1" data-id="element-722">
              {item.description}
            </p>}
          <div className="flex items-center mt-auto pt-1" data-id="element-723">
            <span className="font-semibold text-[#C8A96A] text-sm sm:text-base" data-id="element-724">
              {formatCurrency(item.price)}
            </span>
          </div>
        </div>
      </div>;
  }
  return <div onClick={() => onClick(item)} className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer bg-[#1a1a1a] border border-white/5 active:opacity-90 transition-opacity" data-id="element-725">
      {/* Image */}
      <img src={imageUrl} alt={item.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-726" />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" data-id="element-727" />

      {/* Add Button — always visible on mobile */}
      <button className="absolute top-2.5 right-2.5 w-9 h-9 rounded-full flex items-center justify-center bg-[#006847] text-white z-10 active:scale-90 transition-transform" aria-label="Agregar al pedido" onClick={e => {
      e.stopPropagation();
      onClick(item);
    }} data-id="element-728">
        <Plus className="w-4 h-4" data-id="element-729" />
      </button>

      {/* Content — bottom overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 flex flex-col gap-0.5" data-id="element-730">
        <h3 className="font-bold text-white text-[13px] sm:text-base leading-tight line-clamp-2" data-id="element-731">
          {item.name}
        </h3>
        {item.description && <p className="text-[11px] sm:text-xs text-white/60 line-clamp-1" data-id="element-732">
            {item.description}
          </p>}
        <div className="flex items-center mt-0.5" data-id="element-733">
          <span className="font-bold text-[#F4D35E] text-sm sm:text-base" data-id="element-734">
            {formatCurrency(item.price)}
          </span>
        </div>
      </div>
    </div>;
}